import { Doc, LayoutOrientation } from '@doc';
import { Angle, degrees, point, Point } from '@geom';
import type { Draft } from '@utils';
import { expectDefined } from '@utils';
import { CommandContext } from './CommandContext';
import { revealBounds } from './zoom.commands';

export function toggleBundledRouting(context: CommandContext): { doc: Doc } {
  let { doc } = context;
  const { layouter } = context;

  const oldDoc = doc;

  try {
    doc = doc.produce('toggleBundledRouting', draft => {
      draft.bundleEdges = !draft.bundleEdges;
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    const result = { doc };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return { doc: oldDoc };
  }
}

export function setAlignRoots(
  context: CommandContext,
  value: boolean
): { doc: Doc } | undefined {
  if (context.doc.alignRoots === value) {
    return;
  }

  let { doc } = context;
  const { layouter } = context;

  const oldDoc = doc;

  try {
    doc = doc.produce('setAlignRoots', draft => {
      draft.alignRoots = value;
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    const result = { doc };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}

export function toggleAlignRoots(context: CommandContext): { doc: Doc } {
  return expectDefined(setAlignRoots(context, !context.doc.alignRoots));
}

export function setAlignLeaves(
  context: CommandContext,
  value: boolean
): { doc: Doc } | undefined {
  if (context.doc.alignLeaves === value) {
    return;
  }

  let { doc } = context;
  const { layouter } = context;

  const oldDoc = doc;

  try {
    doc = doc.produce('setAlignLeaves', draft => {
      draft.alignLeaves = value;
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    const result = { doc };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}

export function toggleAlignLeaves(context: CommandContext): { doc: Doc } {
  return expectDefined(setAlignLeaves(context, !context.doc.alignLeaves));
}

export function setOrientation(
  orientation: LayoutOrientation,
  context: CommandContext
): { doc: Doc; pan: Point; zoom: number } | undefined {
  let { doc } = context;

  if (doc.orientation === orientation) {
    return;
  }

  const { layouter } = context;

  const oldDoc = doc;

  try {
    doc = doc.produce('setOrientation', draft => {
      draft.orientation = orientation;
      reorientLayout(draft, oldDoc.orientation, orientation);
    });

    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    let pan: Point = context.pan;
    let zoom: number = context.zoom;

    // Only try to reveal bounds if the document actually contains elements.
    const newBounds = doc.bounds;
    const prevBounds = oldDoc.bounds;

    if (
      orientation !== LayoutOrientation.UNCONSTRAINED &&
      newBounds !== undefined &&
      prevBounds !== undefined
    ) {
      const union = newBounds.union(prevBounds);
      ({ pan, zoom } = revealBounds(union, context));
    }

    const result = { doc, pan, zoom };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}

function reorientLayout(
  draft: Draft<Doc>,
  oldOrientation: LayoutOrientation,
  newOrientation: LayoutOrientation
) {
  if (
    oldOrientation === newOrientation ||
    newOrientation === LayoutOrientation.UNCONSTRAINED
  ) {
    return;
  }

  switch (oldOrientation) {
    case LayoutOrientation.LEFT_TO_RIGHT:
      switch (newOrientation) {
        case LayoutOrientation.RIGHT_TO_LEFT:
          flipNodesHorizontal(draft);
          break;
        case LayoutOrientation.TOP_TO_BOTTOM:
          rotateNodes(draft, degrees(90));
          break;
        case LayoutOrientation.BOTTOM_TO_TOP:
          rotateNodes(draft, degrees(-90));
          break;
      }
      break;
    case LayoutOrientation.RIGHT_TO_LEFT:
      switch (newOrientation) {
        case LayoutOrientation.LEFT_TO_RIGHT:
          flipNodesHorizontal(draft);
          break;
        case LayoutOrientation.TOP_TO_BOTTOM:
          rotateNodes(draft, degrees(-90));
          break;
        case LayoutOrientation.BOTTOM_TO_TOP:
          rotateNodes(draft, degrees(90));
          break;
      }
      break;
    case LayoutOrientation.TOP_TO_BOTTOM:
      switch (newOrientation) {
        case LayoutOrientation.LEFT_TO_RIGHT:
          rotateNodes(draft, degrees(-90));
          break;
        case LayoutOrientation.RIGHT_TO_LEFT:
          rotateNodes(draft, degrees(90));
          break;
        case LayoutOrientation.BOTTOM_TO_TOP:
          flipNodesVertical(draft);
          break;
      }
      break;
    case LayoutOrientation.BOTTOM_TO_TOP:
      switch (newOrientation) {
        case LayoutOrientation.LEFT_TO_RIGHT:
          rotateNodes(draft, degrees(90));
          break;
        case LayoutOrientation.RIGHT_TO_LEFT:
          rotateNodes(draft, degrees(-90));
          break;
        case LayoutOrientation.TOP_TO_BOTTOM:
          flipNodesVertical(draft);
          break;
      }
      break;
  }
}

function flipNodesHorizontal(draft: Draft<Doc>) {
  const bounds = expectDefined(draft.bounds);
  const center = bounds.center;
  for (const node of draft.graph.nodes) {
    const layout = draft.nodeLayout(node);
    const newCenter = point(
      center.x - (layout.bounds.center.x - center.x),
      layout.bounds.center.y
    );
    layout.center = newCenter;
  }
}

function flipNodesVertical(draft: Draft<Doc>) {
  const bounds = expectDefined(draft.bounds);
  const center = bounds.center;
  for (const node of draft.graph.nodes) {
    const layout = draft.nodeLayout(node);
    const newCenter = point(layout.bounds.center.x, center.y - layout.bounds.center.y);
    layout.center = newCenter;
  }
}

function rotateNodes(draft: Draft<Doc>, angle: Angle) {
  const bounds = expectDefined(draft.bounds);
  const center = bounds.center as Point;
  for (const node of draft.graph.nodes) {
    const layout = draft.nodeLayout(node);
    const newCenter = layout.bounds.center.rotateAround(center, angle);
    layout.center = newCenter;
  }
}
